import { useEffect, useState } from "react";
import "@/App.css";
import api from "@/api";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/AuthContext";
import { BookingProvider } from "@/BookingContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import ServicesPage from "@/pages/ServicesPage";
import ProvidersPage from "@/pages/ProvidersPage";
import LocationsPage from "@/pages/LocationsPage";
import PropertyDetail from "@/pages/PropertyDetail";
import ContactPage from "@/pages/ContactPage";
import LegalNotice from "@/pages/LegalNotice";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import OwnerSpace from "@/pages/OwnerSpace";
import AdminPage from "@/pages/AdminPage";
import ReviewPage from "@/pages/ReviewPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const SEO_PATHS = { "/": "home", "/services": "services", "/prestataires": "prestataires", "/locations": "locations", "/contact": "contact" };
const SEO_COMPONENTS = { home: Home, services: ServicesPage, prestataires: ProvidersPage, locations: LocationsPage, contact: ContactPage };
const normalizeSlug = (s) => (s && !s.startsWith("/") ? `/${s}` : s);

function SeoManager({ pages }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pages || pathname.startsWith("/locations/")) return;
    let key = SEO_PATHS[pathname] || null;
    if (!key) {
      const found = Object.entries(pages).find(([, p]) => p.slug && normalizeSlug(p.slug) === pathname);
      key = found ? found[0] : null;
    }
    const meta = (key && pages[key]) || {};
    if (meta.title) document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc && meta.description) desc.setAttribute("content", meta.description);
  }, [pathname, pages]);
  return null;
}

function App() {
  const [seoPages, setSeoPages] = useState(null);
  useEffect(() => {
    api.get("/seo/public").then((r) => setSeoPages(r.data.pages || {})).catch(() => setSeoPages({}));
  }, []);
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BookingProvider>
            <BrowserRouter>
              <ScrollToTop />
              <SeoManager pages={seoPages} />
              <Navbar />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/prestataires" element={<ProvidersPage />} />
                <Route path="/locations" element={<LocationsPage />} />
                <Route path="/locations/:slug" element={<PropertyDetail />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/mentions-legales" element={<LegalNotice />} />
                <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
                <Route path="/espace-proprietaire" element={<OwnerSpace />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/avis/:token" element={<ReviewPage />} />
                {seoPages && Object.entries(seoPages).map(([key, p]) => {
                  const C = SEO_COMPONENTS[key];
                  const alias = normalizeSlug(p.slug);
                  return alias && alias !== p.path && C ? <Route key={key} path={alias} element={<C />} /> : null;
                })}
              </Routes>
              <Footer />
              <Toaster position="top-center" />
            </BrowserRouter>
          </BookingProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
